import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { resetAction } from '@/store/state/rfs/rfs.actions';
import {
  getFormattedAmericanForwardRate,
  getFormattedForwardPoints,
  getFormattedForwardRate,
  getFormattedHistoricalDealOffRate,
  getFormattedPredeliverPoints,
  getFormattedPredeliverRate,
  getFormattedRolloverPointsDisplayed,
  getFormattedRolloverRate,
  getFormattedSpotRate,
  getRolloverImpact,
  selectRfs,
} from '@/store/state/rfs/rfs.selectors';
import { useFormattingContext } from '@/utils/format';
import { type Product, ProductDetails } from './ProductDetails';
import './SuccessTraderModal.scss';
import type { Locale } from '@/utils/locale';
import type { Rfs } from '@/models/rfs';
import { LeftSide, RightSide } from './Shared';
import { routes } from '@/App/routes';
import { selectPredeliverTrade } from '@/store/state/predeliver/predeliver.selectors';
import { selectRolloverTrade } from '@/store/state/rollover/rollover.selectors';
import { selectCurrentLocale } from '@/store/state/ui/ui.selectors';
import { useNavigate } from 'react-router';
import { useAppDispatch } from '@/store/store';
import { isDefined } from '@sgme/fp';
import { selectOppositeCurrencyForTradeType } from '@/store/state/trades/trades.selectors';

const mapProduct = (rfs: Rfs, locale: Locale, fxSpot?: string): Product | undefined => {
  if (rfs.status !== 'executed' && rfs.status !== 'backOfficeValidated') {
    return undefined;
  }
  const spotRate = getFormattedSpotRate(locale)(rfs) || '';
  switch (rfs.product) {
    case 'FxSpot':
      return {
        name: 'FxSpot',
        spotRate,
        sellAmount: rfs.sellAmount,
        sellCurrency: rfs.sellCurrency,
        buyAmount: rfs.buyAmount,
        buyCurrency: rfs.buyCurrency,
      };
    case 'FxFwd':
      return {
        name: 'FxFwd',
        spotRate,
        forwardPoints: getFormattedForwardPoints(locale)(rfs) || '',
        forwardRate: getFormattedForwardRate(locale)(rfs) || '',
        sellAmount: rfs.sellAmount,
        sellCurrency: rfs.sellCurrency,
        buyAmount: rfs.buyAmount,
        buyCurrency: rfs.buyCurrency,
      };
    case 'FxPredeliver':
      return {
        name: 'FxPredeliver',
        spotRate: getFormattedHistoricalDealOffRate(locale)(rfs) || '',
        predeliverPoints: getFormattedPredeliverPoints(locale)(rfs) || '',
        predeliverRate: getFormattedPredeliverRate(locale)(rfs) || '',
        sellAmount: rfs.sellAmount,
        sellCurrency: rfs.sellCurrency,
        buyAmount: rfs.buyAmount,
        buyCurrency: rfs.buyCurrency,
        masterAmount: rfs.masterAmount,
      };
    case 'FxRollover':
      return {
        name: 'FxRollover',
        spotRate: fxSpot ?? spotRate,
        rolloverPoints: getFormattedRolloverPointsDisplayed(locale)(rfs) || '',
        rolloverRate: getFormattedRolloverRate(locale)(rfs) || '',
        sellAmount: rfs.sellAmount,
        sellCurrency: rfs.sellCurrency,
        buyAmount: rfs.buyAmount,
        buyCurrency: rfs.buyCurrency,
        masterAmount: rfs.masterAmount,
      };
    case 'FxAmericanForward':
      return {
        name: 'FxAmericanForward',
        americanForwardRate: getFormattedAmericanForwardRate(locale)(rfs) || '',
        callabilityStart: rfs.callabilityStart,
        sellAmount: rfs.sellAmount,
        sellCurrency: rfs.sellCurrency,
        buyAmount: rfs.buyAmount,
        buyCurrency: rfs.buyCurrency,
      };
    default: {
      const exhaustiveCheck: never = rfs;
      return exhaustiveCheck;
    }
  }
};

export function SuccessTraderModal() {
  const { mapTrade, mapTradeWithOptions, formatAmountWithPrecision, formatAmountWithSign } = useFormattingContext();
  const locale = useSelector(selectCurrentLocale);

  const rfs = useSelector(selectRfs);

  const { buyCurrency, instrument, status, buyAmount: rfsBuyAmount, date: rfsDate, spotDate } = rfs;

  const rolloverTrade = useSelector(selectRolloverTrade);
  const rollover = isDefined(rolloverTrade) ? mapTrade(rolloverTrade) : undefined;
  const rolloverImpact = isDefined(rolloverTrade) ? getRolloverImpact(rfs, rolloverTrade) : undefined;
  const rolloverType = rolloverImpact && rolloverImpact > 0 ? 'greater' : 'lesser';
  const impact = rolloverImpact ? formatAmountWithPrecision(Math.abs(rolloverImpact), 2) : undefined;
  const rolloverCurrency = useSelector(selectOppositeCurrencyForTradeType('rollover'));
  const rolloverMaturityDate = rollover?.maturitydate ? rollover.maturitydate : undefined;

  const isOpen = status === 'executed' || status === 'backOfficeValidated';
  const tradeId = status === 'backOfficeValidated' ? rfs.tradeId : undefined;
  const buyAmount = formatAmountWithPrecision(rfsBuyAmount, 2);

  const predeliverTrade = useSelector(selectPredeliverTrade);
  const predeliver = predeliverTrade
    ? {
        ...mapTradeWithOptions(predeliverTrade, { fullDateFormat: true }),
        remainingAmount: formatAmountWithSign((predeliverTrade.remainingAmount ?? 0) - (predeliverTrade.side === 'buy' ? rfs.buyAmount : rfs.sellAmount)),
      }
    : undefined;

  const isPredeliverOrRollover = !!predeliver || !!rollover;

  const product = mapProduct(rfs, locale, rollover?.strike);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isForward = rfsDate > spotDate;

  const getSuccessModalTitleId = () => {
    if (instrument === 'AmericanForward') {
      return 'trader.successModal.americanForward.title';
    }
    if (predeliver) {
      return 'trader.successModal.predeliver.title';
    }
    if (rollover) {
      return 'trader.successModal.rollover.title';
    }
    if (isForward) {
      return 'trader.successModal.forward.title';
    }
    return 'trader.successModal.title';
  };

  return (
    <Modal isOpen={isOpen} style={{ maxWidth: instrument === 'Cash' ? '500px' : '550px' }} className="SuccessTraderModal">
      <div className="pb-3 flex-column modal-header">
        <h5 className="modal-title display-3">
          <i className="icon icon-xs">check</i>
          <span>
            <FormattedMessage id={getSuccessModalTitleId()} />
          </span>
        </h5>

        {predeliver && (
          <div className="p-0 text-secondary fw-semibold">
            <FormattedMessage id="trader.successModal.predeliverInfo.reference" /> {predeliver.reference}
          </div>
        )}

        {rollover && (
          <div className="p-0 text-secondary fw-semibold">
            <FormattedMessage
              id="trader.successModal.rolloverInfo.reference"
              values={{
                amount: impact,
                currency: rolloverCurrency,
                date: rolloverMaturityDate,
                rolloverType,
              }}
            />
          </div>
        )}
      </div>

      <ModalBody>
        {instrument !== 'AmericanForward' && !rollover && (
          <>
            <LeftSide>
              <FormattedMessage id="trader.successModal.operation" />
            </LeftSide>
            <RightSide>
              <FormattedMessage id="trader.successModal.buy" />
              &nbsp;
              <span data-e2e="buy-amount">{buyAmount}</span>&nbsp;
              <span data-e2e="buy-currency">{buyCurrency}</span>
            </RightSide>
          </>
        )}
        {product && rfsDate && (
          <ProductDetails initialDate={rolloverTrade?.maturitydate} rfsDate={rfsDate} product={product} formatAmountWithPrecision={formatAmountWithPrecision} />
        )}

        <LeftSide withBorder={false}>
          <FormattedMessage id="trader.successModal.reference" values={{ isRollover: isDefined(rollover) }} />
        </LeftSide>
        <RightSide withBorder={false}>{tradeId ?? <span className="loading-blink">--</span>}</RightSide>

        {predeliver ? (
          <div className="col-12 p-0 pt-4 mt-2">
            <FormattedMessage id="trader.successModal.predeliverInfo.remainingAmount" />
            {`${predeliver.remainingAmount} ${predeliver.amountCurrency}`}
            <FormattedMessage id="trader.successModal.predeliverInfo.maturityDate" />
            {predeliver.maturitydate}
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter className="text-end">
        {isPredeliverOrRollover ? (
          <button
            type="button"
            className="btn btn-lg btn-default"
            onClick={() => {
              dispatch(resetAction());
              navigate(routes.PORTFOLIO);
            }}
          >
            {<FormattedMessage id="trader.successModal.goToOperations" />}
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-lg btn-default"
              onClick={() => {
                dispatch(resetAction());
                navigate(routes.HOME);
              }}
            >
              {<FormattedMessage id="trader.successModal.goToHome" />}
            </button>
            <button type="button" className="btn btn-lg btn-success" onClick={() => dispatch(resetAction())}>
              <FormattedMessage id="trader.successModal.close" />
            </button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
